import { KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { InputSenha } from '@/shared/ui/input-senha';

interface Props {
  aberto: boolean;
  /** Mensagem do servidor quando a tentativa anterior errou a senha. */
  erro: string | null;
  /** Muda a cada pedido novo — é o que limpa o campo e destrava o botão. */
  tentativa: number;
  /** `null` = cancelou. */
  aoResponder: (senha: string | null) => void;
}

/**
 * Pede a senha do certificado A1 para UMA assinatura.
 *
 * Aparece para quem escolheu não guardar a senha no perfil — e também para
 * quem guardou, se a senha guardada deixar de abrir o certificado. Quem abre é
 * o `useSenhaDoCertificado`, a partir do código de erro do servidor: a tela não
 * precisa saber de antemão se o médico tem certificado.
 */
export function SenhaDoCertificadoDialogo({ aberto, erro, tentativa, aoResponder }: Props) {
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);

  // O diálogo fica ABERTO enquanto a assinatura roda: senha errada volta como
  // uma tentativa nova, com a mensagem no campo, em vez de fechar e reabrir.
  useEffect(() => {
    setSenha('');
    setEnviando(false);
  }, [tentativa]);

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && !enviando && aoResponder(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Senha do certificado digital
          </DialogTitle>
          <DialogDescription>
            O documento será assinado com o seu certificado A1. A senha é usada só nesta assinatura
            e não fica guardada.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!senha) return;
            setEnviando(true);
            aoResponder(senha);
          }}
        >
          <Campo id="senha-do-certificado" rotulo="Senha" erro={erro ?? undefined}>
            <InputSenha
              id="senha-do-certificado"
              className={CLASSE_CONTROLE}
              value={senha}
              autoComplete="off"
              autoFocus
              onChange={(e) => setSenha(e.target.value)}
            />
          </Campo>

          <p className="text-xs text-muted-foreground">
            Para não digitar a senha a cada documento, guarde-a em Perfil → Assinatura dos documentos.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={enviando}
              onClick={() => aoResponder(null)}
            >
              Cancelar
            </Button>
            <Button type="submit" className="gap-2" disabled={!senha || enviando}>
              {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
              Assinar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
